import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

export default function ImageCropperModal({ 
    show, 
    onHide, 
    imageSrc, 
    onSave, 
    circularCrop = false,
    aspectRatio = undefined 
}) {
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const imgRef = useRef(null);

    // Reset crop on new image
    useEffect(() => {
        setCrop(undefined);
        setCompletedCrop(null);
    }, [imageSrc]);

    function onImageLoad(e) {
        const { width, height } = e.currentTarget;
        if (!width || !height) return;

        let cropSettings;
        if (aspectRatio) {
            cropSettings = centerCrop(
                makeAspectCrop(
                    {
                        unit: '%',
                        width: 90,
                    },
                    aspectRatio,
                    width,
                    height
                ),
                width,
                height
            );
        } else {
            cropSettings = centerCrop(
                {
                    unit: '%',
                    width: 90,
                    height: 90,
                },
                width,
                height
            );
        }

        setCrop(cropSettings);
        setCompletedCrop(convertToPixelCrop(cropSettings, width, height));
    }

    const handleSave = () => {
        const image = imgRef.current;
        if (!image) {
            onHide();
            return;
        }

        let pixelCrop = completedCrop;

        if (!pixelCrop && crop) {
            pixelCrop = convertToPixelCrop(crop, image.width, image.height);
        } else if (pixelCrop && pixelCrop.unit === '%') {
            pixelCrop = convertToPixelCrop(pixelCrop, image.width, image.height);
        }

        if (!pixelCrop || !pixelCrop.width || !pixelCrop.height) {
            onHide();
            return;
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const cropX = pixelCrop.x * scaleX;
        const cropY = pixelCrop.y * scaleY;
        const cropWidth = pixelCrop.width * scaleX;
        const cropHeight = pixelCrop.height * scaleY;

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(cropWidth));
        canvas.height = Math.max(1, Math.round(cropHeight));
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            onHide();
            return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
            image,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            canvas.width,
            canvas.height
        );

        canvas.toBlob((blob) => {
            if (blob) {
                onSave(blob);
            }
            onHide();
        }, 'image/jpeg', 0.95);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Crop Image</Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                {imageSrc ? (
                    <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={(c, percentCrop) => {
                            if (c && c.width && c.height) {
                                setCompletedCrop(c);
                            } else if (percentCrop && imgRef.current) {
                                setCompletedCrop(convertToPixelCrop(percentCrop, imgRef.current.width, imgRef.current.height));
                            }
                        }}
                        aspect={aspectRatio}
                        circularCrop={circularCrop}
                    >
                        <img 
                            ref={imgRef} 
                            src={imageSrc} 
                            alt="Crop preview" 
                            onLoad={onImageLoad}
                            style={{ maxHeight: '60vh', maxWidth: '100%', objectFit: 'contain' }}
                        />
                    </ReactCrop>
                ) : (
                    <div>Loading...</div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Crop & Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
